import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { MaterialItem, StockPiece, OptimizationResult } from '@/types';
import { Loader2, Save, Settings } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useConfig } from '@/hooks/use-config';
import ConfigDialog from './ConfigDialog';
import { toast } from '@/components/ui/use-toast';
import { indexedDBService, SavedList } from '@/lib/indexedDBService';
import { decimalToFraction } from '@/utils/measurementUtils';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CutOptimizerProps {
  items: MaterialItem[];
}

const CutOptimizer: React.FC<CutOptimizerProps> = ({ items }) => {
  const { config, updateConfig, resetConfig } = useConfig();
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [result, setResult] = useState<OptimizationResult | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [listName, setListName] = useState('');

  // Function to get the proper category name
  const getCategoryName = (itemName: string, categoryId: string): string => {
    // Special case for Glass Stop
    if (itemName.includes('Glass Stop') || categoryId === 'glass_stop') {
      return 'Glass Stop';
    }

    // For other categories, use the first word of the item name
    // or extract from known category names
    const knownCategories = {
      'header': 'Header',
      'sill': 'Sill',
      'intermediate': 'Intermediate',
      'jamb': 'Jamb',
      'filler': 'Filler',
      'pan': 'Pan'
    };

    if (knownCategories[categoryId]) {
      return knownCategories[categoryId];
    }

    // Default to first word of item name
    return itemName.split(' ')[0];
  };

  const validateItems = (items: MaterialItem[]): string[] => {
    const errors: string[] = [];

    if (items.length === 0) {
      errors.push('No materials added to optimize.');
      return errors;
    }

    // Check for invalid lengths
    items.forEach(item => {
      if (!item.length || item.length <= 0) {
        errors.push(`"${item.name}" has an invalid length.`);
      }
      if (item.length > config.stockLength) {
        errors.push(`"${item.name}" is longer than the stock length (${config.stockLength} inches).`);
      }
    });

    return errors;
  };

  const generateCutList = () => {
    if (items.length === 0) return;

    // Validate items
    const errors = validateItems(items);
    if (errors.length > 0) {
      errors.forEach(error => toast({ title: "Validation Error", description: error }));
      return;
    }

    setIsOptimizing(true);

    // Simulate optimization calculation time
    setTimeout(() => {
      const optimizationResult = optimizeCuts(items);
      setResult(optimizationResult);
      setIsOptimizing(false);
    }, 1500);
  };

  const optimizeCuts = (items: MaterialItem[]): OptimizationResult => {
    const STOCK_LENGTH = config.stockLength;
    const BLADE_WIDTH = config.bladeWidth;

    // Group items by category
    const itemsByCategory = items.reduce((acc, item) => {
      if (!acc[item.categoryId]) {
        acc[item.categoryId] = [];
      }
      // Expand items based on quantity
      for (let i = 0; i < item.quantity; i++) {
        acc[item.categoryId].push({...item, quantity: 1});
      }
      return acc;
    }, {} as Record<string, MaterialItem[]>);

    let allStockPieces: StockPiece[] = [];
    let totalWaste = 0;

    // Track per-category statistics
    const categoryStats: Record<string, {
      stockCount: number,
      waste: number,
      utilization: number,
      categoryName: string
    }> = {};

    // Process each category separately
    Object.entries(itemsByCategory).forEach(([categoryId, categoryItems]) => {
      // Sort items by length (descending)
      categoryItems.sort((a, b) => b.length - a.length);

      const stockPieces: StockPiece[] = [];
      let currentStock: StockPiece | null = null;
      let categoryWaste = 0;
      let categoryTotalLength = 0;

      // First-fit decreasing algorithm
      categoryItems.forEach(item => {
        const itemLength = item.length;
        let placed = false;

        // Try to place in existing stock pieces
        if (config.useRemnants) {
          for (const stock of stockPieces) {
            if (stock.remainingLength >= itemLength + BLADE_WIDTH) {
              // Place the cut
              const cutPosition = STOCK_LENGTH - stock.remainingLength;
              stock.cuts.push({
                id: uuidv4(),
                itemId: item.id,
                name: item.name,
                length: itemLength,
                position: cutPosition,
              });

              // Update remaining length
              stock.remainingLength -= (itemLength + BLADE_WIDTH);
              placed = true;
              break;
            }
          }
        }

        // If not placed, create a new stock piece
        if (!placed) {
          const newStock: StockPiece = {
            id: uuidv4(),
            categoryId,
            categoryName: getCategoryName(item.name, categoryId), // Get proper category name
            cuts: [{
              id: uuidv4(),
              itemId: item.id,
              name: item.name,
              length: itemLength,
              position: 0,
            }],
            remainingLength: STOCK_LENGTH - itemLength - BLADE_WIDTH,
            utilization: 0, // Will calculate later
            waste: 0, // Will calculate later
          };

          stockPieces.push(newStock);
        }
      });

      // Calculate utilization and waste for each stock piece
      stockPieces.forEach(stock => {
        const usedLength = stock.cuts.reduce((sum, cut) => sum + cut.length, 0);
        const wasteLength = STOCK_LENGTH - usedLength - (stock.cuts.length - 1) * BLADE_WIDTH;

        stock.utilization = (usedLength / STOCK_LENGTH) * 100;
        stock.waste = wasteLength;

        categoryWaste += wasteLength;
        categoryTotalLength += STOCK_LENGTH;
      });

      // Add to overall results
      allStockPieces = [...allStockPieces, ...stockPieces];
      totalWaste += categoryWaste;

      // Calculate category statistics
      categoryStats[categoryId] = {
        stockCount: stockPieces.length,
        waste: categoryWaste,
        utilization: ((categoryTotalLength - categoryWaste) / categoryTotalLength) * 100,
        categoryName: stockPieces[0]?.categoryName || 'Unknown',
      };
    });

    // Calculate overall utilization
    const totalStockLength = allStockPieces.length * STOCK_LENGTH;
    const overallUtilization = totalStockLength > 0
      ? ((totalStockLength - totalWaste) / totalStockLength) * 100
      : 0;

    return {
      stockPieces: allStockPieces,
      totalWaste,
      totalStock: allStockPieces.length,
      overallUtilization,
      categoryStats,
    };
  };

  const handleSaveList = async () => {
    if (!result) return;

    const name = listName.trim() || `Cut List ${new Date().toLocaleDateString()}`;

    const savedList: SavedList = {
      id: uuidv4(),
      name,
      date: Date.now(),
      materials: items,
      result,
    };

    try {
      await indexedDBService.saveCutList(savedList);
      setShowSaveDialog(false);
      setListName('');
      toast({
        title: "List Saved",
        description: `"${name}" has been saved successfully.`,
      });
    } catch (error) {
      console.error('Error saving list:', error);
      toast({
        title: "Save Failed",
        description: "There was an error saving your list. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6 print:space-y-4 print:p-8">
      <div className="flex justify-between items-center print:hidden">
        <div className="flex space-x-2">
          <Button
            onClick={generateCutList}
            disabled={items.length === 0 || isOptimizing}
            className="w-full sm:w-auto"
          >
            {isOptimizing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Optimizing...
              </>
            ) : (
              'Generate Cut List'
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowConfig(true)}
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>

        {result && !isOptimizing && (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSaveDialog(true)}
            >
              <Save className="mr-2 h-4 w-4" />
              Save List
            </Button>
          </div>
        )}
      </div>

      {isOptimizing && (
        <div className="space-y-2">
          <Progress value={45} className="h-2" />
          <p className="text-sm text-center text-muted-foreground">Optimizing your cut list...</p>
        </div>
      )}

      {result && !isOptimizing && (
        <div className="space-y-6">

          {/* Category Breakdown */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Material Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(result.categoryStats).map((stat, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-3 text-lg">{stat.categoryName}</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Stock Lengths Required</p>
                        <p className="font-medium text-xl mt-1">{stat.stockCount}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Material Utilization</p>
                        <p className="font-medium text-xl mt-1">{stat.utilization.toFixed(1)}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Cut List */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Cut List</h3>
              <span className="text-sm text-muted-foreground">
                Total Pieces: {items.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            </div>
            <div className="space-y-6">
              {Object.values(result.categoryStats).map((stat, categoryIndex) => (
                <div key={categoryIndex} className="space-y-2">
                  <h4 className="font-medium">{stat.categoryName}</h4>
                  <div className="space-y-4">
                    {result.stockPieces
                      .filter(stock => stock.categoryName === stat.categoryName)
                      .map((stock, stockIndex) => (
                        <Card key={stockIndex}>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-center mb-2">
                              <h5 className="font-medium">Stock Piece #{stockIndex + 1}</h5>
                              <span className="text-sm text-muted-foreground">
                                Utilization: {stock.utilization.toFixed(1)}%
                              </span>
                            </div>
                            <div className="relative h-12 bg-secondary rounded-md overflow-hidden mb-4 print:mb-2">
                              {stock.cuts.map((cut, cutIndex) => {
                                const startPercent = (cut.position / config.stockLength) * 100;
                                const widthPercent = (cut.length / config.stockLength) * 100;
                                return (
                                  <div
                                    key={cutIndex}
                                    className="absolute h-full bg-primary flex items-center justify-center"
                                    style={{
                                      left: `${startPercent}%`,
                                      width: `${widthPercent}%`,
                                    }}
                                    title={`${cut.name}: ${decimalToFraction(cut.length)}"`}
                                  >
                                    {widthPercent > 5 && (
                                      <span className="text-xs text-white font-medium px-1 truncate">
                                        {decimalToFraction(cut.length)}"
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                              {stock.remainingLength > config.minCutLength && (
                                <div
                                  className="absolute h-full bg-secondary/80 border-l border-dashed border-primary/50 flex items-center justify-center"
                                  style={{
                                    left: `${((config.stockLength - stock.remainingLength) / config.stockLength) * 100}%`,
                                    width: `${(stock.remainingLength / config.stockLength) * 100}%`,
                                  }}
                                >
                                  {(stock.remainingLength / config.stockLength) * 100 > 5 && (
                                    <span className="text-xs text-muted-foreground font-medium px-1">
                                      {decimalToFraction(stock.remainingLength)}"
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="hidden print:block space-y-1">
                              {stock.cuts.map((cut, cutIndex) => (
                                <div key={cutIndex} className="flex justify-between text-sm">
                                  <span>{cut.name}</span>
                                  <span>{decimalToFraction(cut.length)}"</span>
                                </div>
                              ))}
                              {stock.remainingLength > config.minCutLength && (
                                <div className="flex justify-between text-sm text-muted-foreground">
                                  <span>Drop</span>
                                  <span>{decimalToFraction(stock.remainingLength)}"</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Configuration Dialog */}
      <ConfigDialog
        isOpen={showConfig}
        onClose={() => setShowConfig(false)}
        config={config}
        onUpdateConfig={updateConfig}
        onResetConfig={resetConfig}
      />

      {/* Save List Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Cut List</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="list-name">List Name</Label>
            <Input
              id="list-name"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder={`Cut List ${new Date().toLocaleDateString()}`}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveList}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CutOptimizer;
